import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../shared/Layout';
import PageHeader from '../shared/PageHeader';
import IconGoBack from '../../components/icons/IconGoBack';
import IconAdd from '../../components/icons/IconAdd';
import CommunicationTypePicker from './CommunicationTypePicker';
import NotificationBlockCard from './NotificationBlockCard';
import { parseNotificationContent, serializeNotificationBlocks, renderNotificationBlocks } from './renderNotificationBlocks';
import { NOTIFICATION_BLOCK_TYPE_LIST } from '../../variables/notificationBlockTypes';
import * as api from './api';
import '../../css/exercise-templates.css';
import '../../css/communications.css';

let blockKeySeq = 1;
function newBlockId() { return 'b' + (blockKeySeq++) + '-' + Date.now(); }

// Rewritten from a single RichTextEditor field into a real block
// constructor - same "list of typed, addable/reorderable/editable cards"
// shape Topic/Exercise content already uses (topicBlockEditors.jsx/
// TopicElementCard.jsx), applied here via NotificationBlockCard.jsx/
// blockEditors.jsx. `blocks` is the in-memory array; Save serializes it
// back into Notification.content as one JSON string (still a plain TEXT
// column on the backend, no schema change) - there's no per-block backend
// identity, unlike Topic's own blocks, since this whole array is one field.
export default function CommunicationBuilder() {
   const { id } = useParams();
   const navigate = useNavigate();
   const [title, setTitle] = useState('');
   const [type, setType] = useState('INFO');
   const [blocks, setBlocks] = useState([]);
   const [status, setStatus] = useState('loading');
   const [saving, setSaving] = useState(false);
   const [dirty, setDirty] = useState(false);
   const [addPickerOpen, setAddPickerOpen] = useState(false);

   useEffect(() => {
      api.getNotification(id)
         .then((res) => {
            setTitle(res.data.title || '');
            setType(res.data.type || 'INFO');
            setBlocks(parseNotificationContent(res.data.content));
            setStatus('ready');
            setDirty(false);
         })
         .catch((err) => {
            if (err.response && err.response.status === 401) navigate('/login');
            setStatus('error');
         });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [id]);

   const markDirty = (setter) => (value) => { setter(value); setDirty(true); };

   const addBlock = (blockType) => {
      const def = NOTIFICATION_BLOCK_TYPE_LIST.find((t) => t.type === blockType);
      setBlocks((prev) => [...prev, { id: newBlockId(), type: blockType, ...(def ? def.defaultConfig : {}) }]);
      setAddPickerOpen(false);
      setDirty(true);
   };
   const saveBlock = (index, updated) => {
      setBlocks((prev) => prev.map((b, i) => (i === index ? updated : b)));
      setDirty(true);
   };
   const moveBlock = (index, dir) => {
      setBlocks((prev) => {
         const next = [...prev];
         const target = index + dir;
         if (target < 0 || target >= next.length) return prev;
         [next[index], next[target]] = [next[target], next[index]];
         return next;
      });
      setDirty(true);
   };
   const deleteBlock = (index) => {
      setBlocks((prev) => prev.filter((_, i) => i !== index));
      setDirty(true);
   };

   const handleSave = () => {
      if (saving) return;
      setSaving(true);
      api.updateNotification(id, { title, type, content: serializeNotificationBlocks(blocks) })
         .then(() => setDirty(false))
         .catch((err) => console.log(err))
         .finally(() => setSaving(false));
   };

   if (status === 'loading') return <Layout><main className='main-block'><div className='main-content txt-c'>Завантаження...</div></main></Layout>;
   if (status === 'error') return <Layout><main className='main-block'><div className='main-content txt-c'>Повідомлення не знайдено</div></main></Layout>;

   return (
      <Layout>
         <PageHeader name={title || 'Повідомлення'}>
            <div className='exercise-templates-header-actions'>
               <div className='exercise-templates-add-btn' onClick={() => navigate('/communications')} title='До списку'>
                  <IconGoBack />
               </div>
               <button type='button' className='exercise-block-save-btn communication-header-save-btn' disabled={saving || !dirty} onClick={handleSave}>
                  {saving ? 'Збереження...' : 'Зберегти'}
               </button>
            </div>
         </PageHeader>
         <main className='main-block'>
            <div className='main-content txt-c communication-builder-page'>
               <div className='communication-builder-grid'>
                  <div className='communication-builder-form'>
                     <div className='exercise-block-field'>
                        <span className='exercise-block-field-label'>Назва</span>
                        <input className='exercise-block-generic-text' type='text'
                           value={title} onChange={(e) => markDirty(setTitle)(e.target.value)} />
                     </div>
                     <CommunicationTypePicker value={type} onChange={markDirty(setType)} showHint />

                     <p className='exercise-block-field-label' style={{ marginTop: 14 }}>Блоки повідомлення</p>
                     {blocks.map((block, i) => (
                        <NotificationBlockCard key={block.id} block={block}
                           canMoveUp={i > 0} canMoveDown={i < blocks.length - 1}
                           onMove={(dir) => moveBlock(i, dir)}
                           onSave={(updated) => saveBlock(i, updated)}
                           onDelete={() => deleteBlock(i)} />
                     ))}

                     <div className='exercise-block-add-trigger' onClick={() => setAddPickerOpen((prev) => !prev)}>
                        <IconAdd />
                        <span>Додати блок</span>
                     </div>
                     {addPickerOpen ? (
                        <div className='exercise-block-type-picker'>
                           <div className='exercise-block-type-row'>
                              {NOTIFICATION_BLOCK_TYPE_LIST.map((t) => (
                                 <div key={t.type} className='exercise-block-type-chip' style={{ '--accent': t.accent }} onClick={() => addBlock(t.type)}>
                                    {t.label}
                                 </div>
                              ))}
                           </div>
                        </div>
                     ) : ''}
                  </div>
                  <div className='communication-builder-preview'>
                     <p className='exercise-block-field-label'>Попередній перегляд</p>
                     <div className='communication-preview-card'>
                        <p className='communication-preview-title'>{title || 'Без назви'}</p>
                        {renderNotificationBlocks(blocks)}
                     </div>
                  </div>
               </div>
            </div>
         </main>
      </Layout>
   );
}
